import instance from '@src/api/common';
import { IActivateMappedEventPayload } from '@src/pages/metric-templates/types';

import {
  CalculableMetric,
  ESystemType,
  IAttributeTypesPayload,
  ICreateCustomDimensionPayload,
  IFunnelPayload,
  IMetricInit,
  IUpdateCustomDimensionPayload,
  TSystemType,
} from '../types';
import { parseFlowPayload } from '@src/emp-3.0/utils/metrics';
import { AxiosRequestConfig } from 'axios';

export function getMetricList() {
  return instance.get(`/v1/metrics`);
}

export function getMetricSupportedUnits() {
  return instance.get(`/v1/metrics/units`);
}

export function getMappedEventById(id: string) {
  return instance.get(`/v1/mapped_event/${id}`);
}

export interface IBulkActivateMetricsQueryBody {
  metrics: string[];
  use_case_id: number;
  need_deploy?: boolean;
}

export function postBulkActivateMetrics(queryBody: IBulkActivateMetricsQueryBody) {
  return instance.post(`/v1/metrics/bulk_activate`, queryBody);
}

/**
 * description: create a mapped event and deploy it
 * @param queryBody
 */
export function postActivateMappedEvent(queryBody: IActivateMappedEventPayload) {
  return instance.post(`/v1/mapped_event/activate`, queryBody);
}

export function postCheckDuplicateMappedEvent(queryBody: IActivateMappedEventPayload) {
  return instance.post(`/v1/mapped_events/check_duplicate`, queryBody);
}

/**
 * description: update a mapped event and deploy it
 * @param event_id
 * @param queryBody
 */
export function updateActivateMappedEvent(
  event_id: number,
  queryBody: IActivateMappedEventPayload,
) {
  return instance.put(`/v1/mapped_event/${event_id}/activate`, queryBody);
}

export function getCategoryList() {
  return instance.get(`/v1/metrics/categories`);
}

export function postGenericActivateMetrics(data: { metrics: IMetricInit[] }) {
  return instance.post(`/v1/metrics/generic_activate`, data);
}

// Dimensions
export function getCustomDimensionList() {
  return instance.get(`/v1/custom_dimensions`);
}

export function getCustomDimensionById(custom_dimension_id: number) {
  return instance.get(`/v1/custom_dimension/${custom_dimension_id}`);
}

export function getCustomDimensionManagementList() {
  return instance.get(`/v1/management/custom_dimensions`);
}

export function getPropagationDimensions(eventIds: number[]) {
  const eventIdsParam = eventIds.filter((id) => id != null).join(',');
  return instance.get(`/v1/propagation_dimensions?event_ids=${eventIdsParam}`);
}

export function postCustomDimension(data: ICreateCustomDimensionPayload) {
  return instance.post(`/v1/custom_dimension`, data);
}

export function deleteCustomDimension(custom_dimension_id: number) {
  return instance.delete(`/v1/custom_dimension/${custom_dimension_id}`);
}

export function putCustomDimension(
  custom_dimension_id: number,
  data: IUpdateCustomDimensionPayload,
) {
  return instance.put(`/v1/custom_dimension/${custom_dimension_id}`, data);
}

export function deploy(resource: string, payload: { deploy_all: boolean; ids: number[] }) {
  return instance.post(`/v1/deploy/${resource}`, payload);
}

export function undeploy(resource: string, payload: { ids: number[] }) {
  return instance.post(`/v1/undeploy/${resource}`, payload);
}

export function bulkDelete(resource: string, payload: { ids: number[] }) {
  return instance.post(`/v1/delete/${resource}`, payload);
}

export function getSankeyDimensions(src: TSystemType = ESystemType.Trends) {
  return instance.get(`/v1/sankey/dimensions?src=${src}`);
}

export function postShowSankey(src: TSystemType = ESystemType.Trends, payload) {
  return instance.post(`/v1/sankey/query?src=${src}`, payload);
}

export function postShowFunnel(src: TSystemType = ESystemType.Trends, payload) {
  return instance.post(`/v1/funnel/query?src=${src}`, payload);
}

export function getUserIdBySinglePath(src: TSystemType = ESystemType.Trends, payload) {
  return instance.post(`/v1/sankey/query/fields?src=${src}`, payload);
}

export function postFunnel(
  src: TSystemType = ESystemType.Trends,
  is_in_sankey: 0 | 1 = 0, // default 0, When it is 1, backend can't check step
  payload: IFunnelPayload,
) {
  return instance.post(`/v1/funnel/query?src=${src}&is_in_sankey=${is_in_sankey}`, payload);
}

export function getRemainCount() {
  return instance.get(`/v1/custom_dimension/remain_count`);
}

export function getAttributeTypes(interval: string) {
  return instance.get(`/v1/custom_dimension/attribute_types?interval=${interval}`);
}

export function getMappedEventNames(interval: string, event_tag: string) {
  return instance.get(
    `/v1/custom_dimension/mapped_event_names?interval=${interval}&event_tag=${event_tag}`,
  );
}

export function getUserIdByFunnel(src: TSystemType = ESystemType.Trends, payload) {
  return instance.post(`/v1/funnel/query/fields?src=${src}`, payload);
}

export function getRawConfig() {
  return instance.get(`/v1/raw_config`);
}
export function postRawConfig(payload: any) {
  return instance.post(`/v1/raw_config`, payload);
}

export function getSensitiveConfigs(filter) {
  return instance.get(`/v1/sensitive_configs?${filter}`);
}

export function postSensitiveConfig(payload: any) {
  return instance.post(`/v1/sensitive_configs`, payload);
}

export function putSensitiveConfig(configId, payload: any) {
  return instance.put(`/v1/sensitive_config/${configId}`, payload);
}

export function deleteSensitiveConfig(configId) {
  return instance.delete(`/v1/sensitive_config/${configId}`);
}

export function getSensitiveConfigsMeta() {
  return instance.get(`/v1/sensitive_configs/meta`);
}

export function postAttributeTypes(payload: IAttributeTypesPayload) {
  return instance.post(`/v1/custom_dimension/attribute_types`, payload);
}

export function getShareSankey(sankey_share_id: number | string) {
  return instance.get(`/v1/sankey/share/${sankey_share_id}`);
}
export function postShareSankey(payload: any) {
  return instance.post(`/v1/sankey/share`, payload);
}
export function getShareFunnel(funnel_share_id: number | string) {
  return instance.get(`/v1/funnel/share/${funnel_share_id}`);
}
export function postShareFunnel(payload: any) {
  return instance.post(`/v1/funnel/share`, payload);
}

export function getExplorerCurrentIp(config?: AxiosRequestConfig) {
  return instance.get(`/v1/tools/ip`, config);
}

export function getExplorerQuery(payload) {
  return instance.post(`/v1/query?src=event_explorer`, payload);
}

export function postSetWhiteList(payload) {
  return instance.post(`/v1/tools/sampling_white_list`, payload);
}

export function getDraftMetadata() {
  return instance.get(`/v1/deployment/draft_metadata`);
}

// Metric
export function getMetricManagementList() {
  return instance.get(`/v1/management/metrics`);
}

// Flow
export function getFlowList() {
  return instance.get(`/v1/flow`);
}

export function getFlowOutputs() {
  return instance.get(`/v1/flow_outputs`);
}

export function getFlowLabels() {
  return instance.get(`/v1/flow/labels`);
}

export function createFlowLabel(labelName: string) {
  return instance.post(`/v1/flow/labels`, { label: labelName });
}

export function updateFlowLabel(labelId: number, labelName: string) {
  return instance.put(`/v1/flow/label/${labelId}`, { label: labelName });
}

export function deleteFlowLabel(labelId: number) {
  return instance.delete(`/v1/flow/label/${labelId}`);
}

export function getJourneyStages() {
  return instance.get(`/v1/flow/journey_stages`);
}

export function createJourneyStage(journeyStage: string) {
  return instance.post(`/v1/flow/journey_stages`, { journey_stage: journeyStage });
}

export function postCreateFlow(payload) {
  const data = parseFlowPayload(payload);
  return instance.post(`/v1/flow`, data);
}

export function putUpdateFlow(flow_id, payload) {
  const data = parseFlowPayload(payload);
  return instance.put(`/v1/flow/${flow_id}`, data);
}
export function postActivateFlow(payload) {
  const data = parseFlowPayload(payload);
  return instance.post(`/v1/flow/activate`, data);
}
export function putActivateFlow(flow_id, payload) {
  const data = parseFlowPayload(payload);
  return instance.put(`/v1/flow/${flow_id}/activate`, data);
}

export function getFlowDetailsById(flow_id) {
  return instance.get(`/v1/flow/${flow_id}`);
}

export function requestDeleteFlowById(flow_id) {
  return instance.delete(`/v1/flow/${flow_id}`);
}

export function getFlowsManagementList() {
  return instance.get(`/v1/management/flows`);
}

export function getFlowsDetailInManagement(flow_id: number, version_id: number) {
  return instance.get(`/v1/flow/${flow_id}/${version_id}`);
}

export function getEventListForCreateMetric(interval: string) {
  return instance.post(`/v1/events/group_by`, { interval });
}

export function getEventListForCreateFlow(interval: string) {
  return instance.post(`/v1/events`, { interval });
}

export function postGetDataDictionary(payload) {
  return instance.post(`/v1/query?src=event_subject`, payload);
}
export function getValidTimeRange(src: TSystemType = ESystemType.EMP) {
  return instance.get<{ data: { start_time: number; end_time: number } }>(
    `/v1/data_time_range?src=${src}`,
  );
}

export function getCalculableMetrics() {
  return instance.get<{
    data: CalculableMetric[];
  }>(`/v1/metrics/calculable?source=single_event`);
}

export function postFlowValidatePattern(payload: { pattern: string }) {
  return instance.post(`/v1/flow/validate_pattern`, payload);
}
